import { useQuery } from "@apollo/client";
import moment from "moment";
import React, { useState } from "react";
import { useCollapse } from "react-collapsed";
import { Box, Flex, Text } from "rebass/styled-components"
import { Skeleton } from "../../components/loadingSkeleton/Skeleton";
import { GET_PATIENT_SYMPTOMS } from "../../graphql/queries";
import { IGetProviderPatientOverallSymptomsInput, IGetProviderPatientOverallSymptomsOutput, ISymptomTrackerCardCategory, ISymptomTrackerMainChartEntry } from "../../graphql/types/queries/symptoms";
import { IOptionsDaysBack } from "../Patient";
import SymptomsChart, { SymptomsChartLoading } from "./components/SymptomsChart";
import Modal from "styled-react-modal";
import { SpecialModalBackground } from "../../components/Commons";
import styled from "styled-components";

const Wrapper = styled(Flex)`
  overflow: auto;
  width: 90%;
  display: block;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;
const Container = styled(Flex)`
overflow-y: auto;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

interface SymptomsProps {
  id: string,
  selectedPeriod: IOptionsDaysBack
  isMinimized?: boolean
}

export interface IFlattenCategory {
  values: {
    date: string;
    values: number | null;
    hasLabs?: boolean,
    day: string,
    month?: string | null
  }[];
  id: string;
  name: string;
  color?: string | undefined;
  textLineColor?: string | undefined;
  category: string
}

const flattenCategories = (categories: Array<ISymptomTrackerCardCategory> | null | undefined, global: Array<ISymptomTrackerMainChartEntry> | null | undefined, patientId: string, daysBack = 7): Array<IFlattenCategory> => {
  if (!categories && !global) return []

  let perDay: Array<IFlattenCategory> = []

  let lastAvgGlobal: number | null
  if (!!global) {
    perDay.push({
      values: global.map(x => {
        if (x.average !== undefined && x.average !== null) {
          lastAvgGlobal = x.average
        }
        const m1 = moment(x.date, 'YYYY-MM-DD')
        return {
          date: x.date,
          values: x.average === undefined || x.average === null ? lastAvgGlobal : x.average,
          hasLabs: x.hasLabs,
          day: m1.format('DD'),
          month: m1.format('DD') === '01' ? m1.format('MMMM') : null
        }
      }),
      id: `global${patientId}`,
      name: 'Overall Symptoms',
      color: '#2196F3',
      textLineColor: '#2196F3',
      category: 'global'
    })
  }

  if (!categories) {
    return perDay
  }

  for (let index = 0; index < categories.length; index++) {
    const category = categories[index];
    if (!category.values) {
      perDay.push({
        ...category,
        category: category.name,
        values: []
      })

      continue
    }

    const cat: Array<{ date: string, values: Array<number | null | undefined> }> = []

    for (let index2 = 0; index2 < category.values.length; index2++) {
      const symptoms = category.values[index2];
      if (!symptoms.values) continue
      for (let index3 = 0; index3 < symptoms.values.length; index3++) {
        const symptom = symptoms.values[index3];
        let indexDay = cat.findIndex(x => x.date === symptom.date)
        if (indexDay < 0) {
          cat.push({
            date: symptom.date,
            values: [symptom.values]
          })
        } else {
          if (!cat[indexDay].values) {
            cat[indexDay].values = [symptom.values]
          }
          cat[indexDay].values.push(symptom.values)
        }
      }
    }

    let lastAvg: number | null
    perDay.push({
      ...category,
      category: category.name,
      values: cat.map((x, i, arr) => {
        const notnull = x.values.filter(x => x !== null && x !== undefined)
        const m1 = moment(x.date, 'YYYY-MM-DD')
        if (notnull.length < 1) {
          return {
            date: x.date,
            values: lastAvg,
            day: m1.format('DD'),
            month: m1.format('DD') === '01' ? m1.format('MMMM') : null
          }
        }
        let al = 0
        for (let index = 0; index < notnull.length; index++) {
          const element = notnull[index];
          if (element !== null && element !== undefined) {
            al = al + element
          }
        }
        const avgDay = al / notnull.length
        lastAvg = avgDay
        return {
          date: x.date,
          values: avgDay,
          day: m1.format('DD'),
          month: m1.format('DD') === '01' ? m1.format('MMMM') : null
        }
      })
    })

  }

  return perDay
}

const flattenSymptoms = (categories: Array<ISymptomTrackerCardCategory> | null | undefined, patientId: string): Array<IFlattenCategory> => {
  if (!categories) return []

  let perDay: Array<IFlattenCategory> = []

  if (!categories) {
    return perDay
  }

  for (let index = 0; index < categories.length; index++) {
    const category = categories[index];
    if (!category.values) {

      continue
    }

    for (let index2 = 0; index2 < category.values.length; index2++) {
      const symptoms = category.values[index2];
      if (!symptoms.values) continue
      let lastAvg: number | null = null
      // for (let irepeat = 0; irepeat < 5; irepeat++) {
      let ee = {
        values: symptoms.values.map(x => {
          if (x.values !== undefined && x.values !== null) {
            lastAvg = x.values
          }
          const m1 = moment(x.date, 'YYYY-MM-DD')
          return {
            date: x.date,
            values: x.values === undefined || x.values === null ? lastAvg : x.values,
            day: m1.format('DD'),
            month: m1.format('DD') === '01' ? m1.format('MMMM') : null
          }
        }),
        id: `${symptoms.id}${patientId}`,
        name: symptoms.name,
        color: category.color,
        textLineColor: category.textLineColor,
        category: category.name
      }
      perDay.push(ee)
    }

  }

  return perDay
}


export const Symptoms: React.FC<SymptomsProps> = ({ id, selectedPeriod, isMinimized }) => {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse()
  const [expandedItem, setExpandedItem] = useState<string | null | undefined>()
  const [forMinimizedItem, setForMinimizedItem] = useState<IFlattenCategory>()
  const { data, loading } = useQuery<IGetProviderPatientOverallSymptomsOutput, IGetProviderPatientOverallSymptomsInput>(GET_PATIENT_SYMPTOMS, { variables: { id } })

  if (!!loading) {
    return (
      <Flex marginRight={[0, 4, 4]} width='100%' mb={4} flexDirection='column'>
        <Flex width={'100%'} flexWrap='wrap'>
          {[0, 1, 2].map(x => {
            return <SymptomsChartLoading key={`loadingChart${x}`} />
          })}
        </Flex>
        <Flex style={{ alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} mt={3} mb={3} mr={'18px'}>
          <Skeleton height={25} width={150} />
        </Flex>
      </Flex>)
  }

  if (!data || !data.getProviderPatientOverallSymptoms) return null

  const byCats = flattenCategories(data?.getProviderPatientOverallSymptoms?.details, data?.getProviderPatientOverallSymptoms?.global, id)
  let bySymptom = flattenSymptoms(data?.getProviderPatientOverallSymptoms?.details, id)

  if ((byCats.length < 1 && bySymptom.length < 1) || (data?.getProviderPatientOverallSymptoms?.global.length < 1)) {
    return <Text textAlign={'center'} style={{ opacity: 0.3 }} mb={4}>No symptoms tracked</Text>
  }

  return (
    <Flex flexDirection={'column'} flex={1} width={'100%'} overflow={'hidden'}>
      <Container marginRight={[0, 4, 4]} width='100%' mb={4} flexDirection='column'>
        <Flex width={'100%'} flexWrap='wrap'>

          {byCats && (
            <>

              {byCats.map((x, i, aa) => {
                let expandedRow: number | null = null

                if (!!expandedItem) {
                  expandedRow = parseInt((aa.findIndex(x => x.id === expandedItem) / 3).toString())
                }
                const currentRow = parseInt(((i) / 3).toString())

                return (<React.Fragment key={x.id}>
                  {(i + 1) % 3 === 1 &&
                    <Flex key={`reextension${currentRow}`} width="100%">
                      {expandedRow !== null && currentRow === expandedRow && !!expandedItem && (i + 1) % 3 === 1 && aa.findIndex(x => x.id === expandedItem) > -1 && (
                        <SymptomsChart selectedPeriod={selectedPeriod} key={`re${x}`} expandedItem={expandedItem} setExpandedItem={(id: string | null | undefined) => {
                          setExpandedItem(id)
                          if (isMinimized) {
                            if (!!id) {
                              const itm = aa.find(x => x.id === expandedItem)!
                              setForMinimizedItem(itm)
                            } else {
                              setForMinimizedItem(undefined)
                            }
                          }
                        }} item={aa.find(x => x.id === expandedItem)!} isExpansion />
                      )}
                    </Flex>
                  }
                  <SymptomsChart selectedPeriod={selectedPeriod} key={`r${x}`} expandedItem={expandedItem} setExpandedItem={(id: string | null | undefined) => {
                    setExpandedItem(id)
                    if (isMinimized) {
                      if (!!id) {
                        setForMinimizedItem(x)
                      } else {
                        setForMinimizedItem(undefined)
                      }
                    }
                  }} item={x} />
                </React.Fragment>)
              })}
            </>
          )}
        </Flex>

        {bySymptom && (
          <>
            <Flex {...getToggleProps()} style={{ alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-end' }} mt={3} mb={3} mr={'18px'}>
              <Text mr={3} fontWeight={500} color='#2196F3'>{!isExpanded ? 'Expand all symptoms' : 'Collapse symptoms'}</Text>
              {!isExpanded ? (
                <svg width="10" height="10" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.4375 5.34375L12.4688 4.375L7.5 9.34375L2.53125 4.375L1.5625 5.34375L7.5 11.25L13.4375 5.34375Z" fill="#2196F3" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5625 9.65625L2.53125 10.625L7.5 5.65625L12.4687 10.625L13.4375 9.65625L7.5 3.75L1.5625 9.65625Z" fill="#2196F3" />
                </svg>

              )}

            </Flex>
            <div  {...getCollapseProps()}>
              <Flex width={'100%'} flexWrap='wrap'>
                <>

                  {bySymptom.map((x, i, bb) => {
                    let expandedRow: number | null = null

                    if (!!expandedItem) {
                      expandedRow = parseInt((bb.findIndex(x => x.id === expandedItem) / 3).toString())
                    }
                    const currentRow = parseInt(((i) / 3).toString())

                    return (<React.Fragment key={x.id}>
                      {(i + 1) % 3 === 1 &&
                        <Flex key={`reextension${currentRow}`} width="100%">
                          {expandedRow !== null && currentRow === expandedRow && !!expandedItem && (i + 1) % 3 === 1 && bb.findIndex(x => x.id === expandedItem) > -1 && (
                            <SymptomsChart selectedPeriod={selectedPeriod} key={`re${x}`} expandedItem={expandedItem} setExpandedItem={(id: string | null | undefined) => {
                              setExpandedItem(id)
                              if (isMinimized) {
                                if (!!id) {
                                  const itm = bb.find(x => x.id === expandedItem)!
                                  setForMinimizedItem(itm)
                                } else {
                                  setForMinimizedItem(undefined)
                                }
                              }
                            }} item={bb.find(x => x.id === expandedItem)!} isExpansion />
                          )}
                        </Flex>
                      }
                      <SymptomsChart selectedPeriod={selectedPeriod} key={`r${x}`} expandedItem={expandedItem} setExpandedItem={(id: string | null | undefined) => {
                        setExpandedItem(id)
                        if (isMinimized) {
                          if (!!id) {
                            setForMinimizedItem(x)
                          } else {
                            setForMinimizedItem(undefined)
                          }
                        }
                      }} item={x} />
                    </React.Fragment>)
                  })}
                </>
              </Flex>
            </div>
          </>
        )}
        <Box height='150px' />

        {!!isMinimized && !!expandedItem && !!forMinimizedItem && (
          <Modal
            isOpen={true}
            onBackgroundClick={() => {
              setExpandedItem(null);
              setForMinimizedItem(undefined)
            }}
            onEscapeKeydown={() => {
              setExpandedItem(null);
              setForMinimizedItem(undefined)
            }}
          >
            <SpecialModalBackground
              onClick={() => {
                setExpandedItem(null);
                setForMinimizedItem(undefined)
              }}
            >
              {/* <Box
              style={{
                margin: '0 auto',
                alignSelf: 'center',
                borderRadius: '20px',
                position: 'relative',
                background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                maxWidth: '95vw',
                minWidth: 'initial',
                maxHeight: '95vh',
                minHeight: '50vh',
                overflowY: 'hidden',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            > */}
              <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto', margin: '0 auto' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}>
                <SymptomsChart selectedPeriod={selectedPeriod} key={`re2${expandedItem}`} expandedItem={expandedItem} setExpandedItem={(id: string | null | undefined) => {
                  setExpandedItem(null)
                  setForMinimizedItem(undefined)
                }} item={forMinimizedItem} isExpansion />

              </Wrapper>
              {/* </Box> */}
            </SpecialModalBackground>
          </Modal>
        )}
      </Container>
    </Flex>
  )
}